import { Car } from '../Model/Car';
import { LogbookEntry } from '../Model/LogbookEntry';

// ─── DTOs that mirror the backend ApiResponse<T> envelope ────────────────────

export interface ApiResponse<T> {
    success: boolean;
    data?: T;
    message?: string;
    errorCode?: string;
    timestamp: string;
}

export interface User {
    partitionKey: string;
    rowKey: string;
    email: string;
    displayName?: string;
    isAdmin: boolean;
}

export interface LogbookFilters {
    driverId?: string;
    carId?: string;
    from?: string; // ISO date string yyyy-MM-dd
    to?: string;   // ISO date string yyyy-MM-dd
    top?: number;  // limit results (server sorts by timestamp desc before applying)
}

// ─── Raw API shapes (server uses camelCase via Newtonsoft) ────────────────────

interface RawCar {
    rowKey: string;
    partitionKey: string;
    licensePlate: string;
    kilometers: number;
    carName: string;
}

interface RawLogbookEntry {
    rowKey: string;
    partitionKey: string;
    envelopeCode: string;
    kilometersState: number;
    kilometersBillable: number;
    kilometersPersonalWriteoff?: number;
    kilometersCompanyWriteoff?: number;
    kilometersGeneralWriteoff?: number;
    timestamp?: string;
    carId: string;
    driverId: string;
    purpose: string;
    rate: number;
    depositAmount?: number;
    additionalExpenses?: number;
    previouslyAgreedPrice: number;
    cardPayments: number;
    markedChecked: boolean;
    finalPrice: number;
}

interface RawRate {
    rowKey: string;
    partitionKey: string;
    purpose: string;
    rateType: 'Taxi' | 'Service' | 'Personal' | 'Contract';
    dayRate: number;
    nightRate: number;
    sortOrder: number;
}

export interface Rate {
    id: string;
    purpose: string;
    rateType: 'Taxi' | 'Service' | 'Personal' | 'Contract';
    dayRate: number;
    nightRate: number;
    sortOrder: number;
}

interface RawUser {
    rowKey: string;
    partitionKey: string;
    email: string;
    displayName?: string;
    isAdmin: boolean;
}

// ─── Mapping helpers ──────────────────────────────────────────────────────────

function mapCar(raw: RawCar): Car {
    return {
        rowKey: raw.rowKey,
        partitionKey: raw.partitionKey,
        licensePlate: raw.licensePlate ?? raw.rowKey,
        kilometers: raw.kilometers,
        carName: raw.carName,
    };
}

function mapEntry(raw: RawLogbookEntry): LogbookEntry {
    return {
        id: raw.rowKey,
        envelope: raw.envelopeCode,
        timestamp: raw.timestamp ? new Date(raw.timestamp) : undefined,
        kilometersState: raw.kilometersState,
        kilometersBillable: raw.kilometersBillable,
        kilometersPersonalWriteoff: raw.kilometersPersonalWriteoff ?? 0,
        kilometersCompanyWriteoff: raw.kilometersCompanyWriteoff ?? 0,
        kilometersGeneralWriteoff: raw.kilometersGeneralWriteoff ?? 0,
        carId: raw.carId,
        driverId: raw.driverId,
        purpose: raw.purpose as LogbookEntry['purpose'],
        rate: raw.rate,
        depositAmount: raw.depositAmount ?? null,
        additionalExpenses: raw.additionalExpenses ?? null,
        previouslyAgreedPrice: raw.previouslyAgreedPrice ?? null,
        cardPayments: raw.cardPayments ?? null,
        rowChecked: raw.markedChecked,
        finalPrice: raw.finalPrice,
    };
}

function mapRate(raw: RawRate): Rate {
    return {
        id: raw.rowKey,
        purpose: raw.purpose,
        rateType: raw.rateType ?? 'Taxi',
        dayRate: raw.dayRate,
        nightRate: raw.nightRate,
        sortOrder: raw.sortOrder ?? 0,
    };
}

function mapUser(raw: RawUser): User {
    return {
        rowKey: raw.rowKey,
        partitionKey: raw.partitionKey,
        email: raw.email ?? raw.rowKey,
        displayName: raw.displayName || undefined,
        isAdmin: raw.isAdmin,
    };
}

// ─── ApiService ───────────────────────────────────────────────────────────────

export class ApiService {
    private baseUrl: string;
    private token: string;
    private onUnauthorized?: () => void;

    constructor(baseUrl: string, token: string, onUnauthorized?: () => void) {
        // Ensure no trailing slash
        this.baseUrl = baseUrl.replace(/\/$/, '');
        this.token = token;
        this.onUnauthorized = onUnauthorized;
    }

    // ── Core request helper ──────────────────────────────────────────────────

    private async request<T>(
        path: string,
        options: RequestInit = {}
    ): Promise<ApiResponse<T>> {
        const url = `${this.baseUrl}${path}`;
        const response = await fetch(url, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${this.token}`,
                ...options.headers,
            },
        });

        if (!response.ok) {
            if (response.status === 401 && this.onUnauthorized) {
                this.onUnauthorized();
            }
            let msg = `HTTP ${response.status}`;
            try {
                const body = await response.json();
                msg = body?.message ?? body?.title ?? msg;
            } catch {
                // ignore parse errors
            }
            throw new Error(msg);
        }

        return response.json() as Promise<ApiResponse<T>>;
    }

    private async requestData<T>(path: string, options?: RequestInit): Promise<T> {
        const res = await this.request<T>(path, options);
        if (!res.success || res.data === undefined) {
            throw new Error(res.message ?? 'API returned unsuccessful response');
        }
        return res.data;
    }

    // ── Cars ─────────────────────────────────────────────────────────────────

    async getCars(): Promise<Car[]> {
        const raw = await this.requestData<RawCar[]>('/cars');
        return raw.map(mapCar);
    }

    async getCar(id: string): Promise<Car> {
        const raw = await this.requestData<RawCar>(`/cars/${id}`);
        return mapCar(raw);
    }

    async createCar(car: Partial<Car>): Promise<Car> {
        const raw = await this.requestData<RawCar>('/cars', {
            method: 'POST',
            body: JSON.stringify(car),
        });
        return mapCar(raw);
    }

    async updateCar(id: string, car: Partial<Car>): Promise<Car> {
        const raw = await this.requestData<RawCar>(`/cars/${id}`, {
            method: 'PUT',
            body: JSON.stringify(car),
        });
        return mapCar(raw);
    }

    async deleteCar(id: string): Promise<void> {
        await this.request(`/cars/${id}`, { method: 'DELETE' });
    }

    // ── Logbook Entries ───────────────────────────────────────────────────────

    async getLogbookEntries(filters?: LogbookFilters): Promise<LogbookEntry[]> {
        const params = new URLSearchParams();
        if (filters?.driverId) params.set('driverId', filters.driverId);
        if (filters?.carId) params.set('carId', filters.carId);
        if (filters?.from) params.set('from', filters.from);
        if (filters?.to) params.set('to', filters.to);
        if (filters?.top) params.set('top', filters.top.toString());
        const qs = params.toString() ? `?${params.toString()}` : '';
        const raw = await this.requestData<RawLogbookEntry[]>(`/logbook-entries${qs}`);
        return raw.map(mapEntry);
    }

    async getLogbookEntry(id: string): Promise<LogbookEntry> {
        const raw = await this.requestData<RawLogbookEntry>(`/logbook-entries/${id}`);
        return mapEntry(raw);
    }

    async createLogbookEntry(entry: Partial<LogbookEntry>): Promise<LogbookEntry> {
        const raw = await this.requestData<RawLogbookEntry>('/logbook-entries', {
            method: 'POST',
            body: JSON.stringify(entry),
        });
        return mapEntry(raw);
    }

    async updateLogbookEntry(id: string, entry: Partial<LogbookEntry>): Promise<LogbookEntry> {
        const raw = await this.requestData<RawLogbookEntry>(`/logbook-entries/${id}`, {
            method: 'PUT',
            body: JSON.stringify(entry),
        });
        return mapEntry(raw);
    }

    async deleteLogbookEntry(id: string): Promise<void> {
        await this.request(`/logbook-entries/${id}`, { method: 'DELETE' });
    }

    async markEntryChecked(id: string, markedChecked: boolean): Promise<LogbookEntry> {
        const raw = await this.requestData<RawLogbookEntry>(`/logbook-entries/${id}/mark-checked`, {
            method: 'PATCH',
            body: JSON.stringify({ markedChecked }),
        });
        return mapEntry(raw);
    }

    // ── Users ─────────────────────────────────────────────────────────────────

    async getUsers(): Promise<User[]> {
        const raw = await this.requestData<RawUser[]>('/users');
        return raw.map(mapUser);
    }

    async createUser(email: string, isAdmin: boolean, password: string, displayName?: string): Promise<User> {
        const raw = await this.requestData<RawUser>('/users', {
            method: 'POST',
            body: JSON.stringify({ email, isAdmin, password, displayName: displayName || undefined }),
        });
        return mapUser(raw);
    }

    async updateUser(email: string, isAdmin: boolean, displayName?: string): Promise<User> {
        const raw = await this.requestData<RawUser>(`/users/${encodeURIComponent(email)}`, {
            method: 'PUT',
            body: JSON.stringify({ email, isAdmin, displayName: displayName ?? null }),
        });
        return mapUser(raw);
    }

    async deleteUser(email: string): Promise<void> {
        await this.request(`/users/${encodeURIComponent(email)}`, { method: 'DELETE' });
    }

    // ── Rates ─────────────────────────────────────────────────────────────────

    async getRates(): Promise<Rate[]> {
        const raw = await this.requestData<RawRate[]>('/rates');
        return raw.map(mapRate);
    }

    async createRate(rate: Omit<Rate, 'id' | 'sortOrder'>): Promise<Rate> {
        const raw = await this.requestData<RawRate>('/rates', {
            method: 'POST',
            body: JSON.stringify(rate),
        });
        return mapRate(raw);
    }

    async updateRate(id: string, rate: Omit<Rate, 'id' | 'sortOrder'>): Promise<Rate> {
        const raw = await this.requestData<RawRate>(`/rates/${encodeURIComponent(id)}`, {
            method: 'PUT',
            body: JSON.stringify(rate),
        });
        return mapRate(raw);
    }

    async deleteRate(id: string): Promise<void> {
        await this.request(`/rates/${encodeURIComponent(id)}`, { method: 'DELETE' });
    }

    async reorderRates(order: { id: string; sortOrder: number }[]): Promise<void> {
        await this.request('/rates/reorder', {
            method: 'POST',
            body: JSON.stringify(order),
        });
    }

    // ── Excel Export ──────────────────────────────────────────────────────────

    async exportLogbook(carId: string, year: number): Promise<Blob> {
        const url = `${this.baseUrl}/logbook-entries/export?carId=${encodeURIComponent(carId)}&year=${year}`;
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${this.token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401 && this.onUnauthorized) {
                this.onUnauthorized();
            }
            let msg = `HTTP ${response.status}`;
            try {
                const body = await response.json();
                msg = body?.message ?? body?.title ?? msg;
            } catch {
                // ignore parse errors
            }
            throw new Error(msg);
        }

        return response.blob();
    }
}

// ─── Singleton ────────────────────────────────────────────────────────────────

let _instance: ApiService | null = null;

export function initApiService(baseUrl: string, token: string, onUnauthorized?: () => void): ApiService {
    _instance = new ApiService(baseUrl, token, onUnauthorized);
    return _instance;
}

export function getApiService(): ApiService {
    if (!_instance) throw new Error('ApiService not initialized. Call initApiService() first.');
    return _instance;
}
